import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Text} from '@Components/text';
import type {DesignMeta, GraphicMeta, PosterHashedId} from '@Components/social-media/publish-params.types';
import {getPosterPlaceholderImagePreviewURL, getPosterPreviewURL} from '@Libraries/poster-library';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import styles from './social-media-wizard-design-selector-preview.module.scss';
import {type Active, defaultDropAnimationSideEffects, DndContext, DragOverlay, type DropAnimation, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {horizontalListSortingStrategy, SortableContext} from '@dnd-kit/sortable';
import {SocialMediaPreviewItem} from '@Components/social-media/components/social-media-wizard-design-selector-preview/components/social-media-preview-item/social-media-preview-item';

export interface SocialMediaWizardDesignSelectorPreviewProps {
  designIdsToPublishTo: string[];
  onUpdateDesignIdsToPublishTo?: (designIds: string[]) => void;
  containerClasses?: string;
}

const containerQuery: ContainerQueryObject = {
  xsmall: {
    minWidth: 0,
    maxWidth: 480,
  },
  small: {
    minWidth: 481,
    maxWidth: 783,
  },
  medium: {
    minWidth: 784,
    maxWidth: 945,
  },
  large: {
    minWidth: 946,
    maxWidth: 1149,
  },
  xlarge: {
    minWidth: 1150,
  },
};

export function SocialMediaWizardDesignSelectorPreview({
  designIdsToPublishTo,
  onUpdateDesignIdsToPublishTo = (designIds: string[]): void => {},
  containerClasses = '',
}: SocialMediaWizardDesignSelectorPreviewProps): ReactElement {
  const [designsGraphicMetaState, setDesignsGraphicMetaState] = useState<Record<PosterHashedId, GraphicMeta>>({});
  const previewItemsContainerRef = useRef<HTMLDivElement>(null);
  const [shouldShowLeftArrow, setShouldShowLeftArray] = useState(false);
  const [shouldShowRightArray, setShouldShowRightArray] = useState(false);
  const idPrefix = `design-id`;
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);
  const dragPosterIndex = useRef<number>(0);
  const scrollOffset = 56;
  const [showDiv, setShowDiv] = useState(false);
  const [active, setActive] = useState<Active | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
  const activeItem = useMemo(() => designIdsToPublishTo.find((item) => item === active?.id), [active, designIdsToPublishTo]);
  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0',
        },
      },
    }),
  };

  useEffect((): (() => void | undefined) | undefined => {
    if (designIdsToPublishTo.length > 0) {
      const timer = setTimeout(() => {
        setShowDiv(true);
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }

    return undefined;
  }, [designIdsToPublishTo]);

  const getExistingDesignIds = (): string[] => {
    return Object.keys(designsGraphicMetaState);
  };

  const getDesignIdsAdded = (): string[] => {
    const existingDesignIds = getExistingDesignIds();
    return designIdsToPublishTo.filter((designId) => {
      return !existingDesignIds.includes(designId);
    });
  };

  const getDesignIdRemoved = (): string[] => {
    const existingDesignIds = getExistingDesignIds();
    return existingDesignIds.filter((designId) => {
      return !designIdsToPublishTo.includes(designId);
    });
  };

  const generateDummyValues = (designIds: string[]): Record<PosterHashedId, GraphicMeta> => {
    const dummyValues: Record<PosterHashedId, GraphicMeta> = {};
    designIds.forEach((id) => {
      dummyValues[id] = {isVideo: false, graphicURL: '', name: ''};
    });
    return dummyValues;
  };

  const updateStateForDesignsRemoved = (designsRemoved: string[], designsAdded: string[]): void => {
    setDesignsGraphicMetaState((prevState) => {
      const newState = {...prevState};

      designsRemoved.forEach((id) => {
        delete newState[id];
      });

      const dummyValues = generateDummyValues(designsAdded);

      Object.keys(dummyValues).forEach((id) => {
        newState[id] = dummyValues[id];
      });

      return newState;
    });
  };

  const getGraphicMeta = async (designId: string): Promise<GraphicMeta> => {
    const data = (await window.PMW.writeLocal('socialmedia/getDesignMeta', {
      designId,
    })) as DesignMeta;

    const previewUrl = getPosterPreviewURL(designId, data.isVideo);

    return {graphicURL: previewUrl, isVideo: data.isVideo, name: data.designName};
  };

  const updateGraphicMetaForId = (posterHashedIdToUpdate: string, updatedGraphicMeta: GraphicMeta): void => {
    setDesignsGraphicMetaState((prevState) => {
      const newState = {...prevState};
      newState[posterHashedIdToUpdate] = updatedGraphicMeta;
      return newState;
    });
  };

  const getGraphicMetaForAllDesigns = async (designIds: string[]): Promise<void> => {
    try {
      const promises = designIds.map((designId) => {
        return getGraphicMeta(designId);
      });

      const results = await Promise.all(promises);

      results.forEach((graphicMeta, index) => {
        updateGraphicMetaForId(designIds[index], graphicMeta);
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const updateDesignsGraphicsMetaIfNeeded = (): void => {
    const designsAdded = getDesignIdsAdded();
    const designsRemoved = getDesignIdRemoved();
    updateStateForDesignsRemoved(designsRemoved, designsAdded);
    void getGraphicMetaForAllDesigns(designsAdded);
  };

  useEffect(() => {
    updateDesignsGraphicsMetaIfNeeded();
    handlePreviewItemsScroll();
  }, [designIdsToPublishTo]);

  const getTextContainer = (): ReactElement => {
    if (isMobileView()) {
      return (
        <div className={styles.mobileTextContainer}>
          <Text className="body-s content-body" bold val={window.i18next.t('pmwjs_selected_designs')} />
          <OverlayTrigger
            trigger={['hover', 'focus', 'click']}
            overlay={
              <Tooltip id="tooltip-overlay-info" className={`_pointer-events-none ${styles.customTooltip} spacing-m-b-1`} style={{position: 'fixed'}}>
                {window.i18next.t('pmwjs_rearrange_selection')}
              </Tooltip>
            }
            delay={{show: 50, hide: 50}}
            placement="top"
            popperConfig={undefined}
            show={undefined}
            defaultShow={undefined}
            onHide={undefined}
            onToggle={undefined}
            target={undefined}
            flip={undefined}
            offset={undefined}
          >
            <Icon size={IconSize.SIZE_ICON_20} icon="icon-info" />
          </OverlayTrigger>
        </div>
      );
    }

    return (
      <div className={`${styles.textContainer} flex-column-justify-between`}>
        <Text className="body-s content-body" bold val={window.i18next.t('pmwjs_selected_designs')} />
        <Text className="body-xs content-sub-text" val={window.i18next.t('pmwjs_rearrange_selection')} />
      </div>
    );
  };

  const scrollTo = (scrollLeft: number): void => {
    if (previewItemsContainerRef.current) {
      previewItemsContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const onClickRightArrow = (): void => {
    if (previewItemsContainerRef?.current) {
      const newScrollLeft = previewItemsContainerRef.current.scrollLeft + scrollOffset;
      scrollTo(newScrollLeft);
    }
  };

  const getRightScrollArrow = (): ReactElement | null => {
    if (!shouldShowRightArray) {
      return null;
    }

    return (
      <Icon
        icon="icon-caret-right"
        onClick={onClickRightArrow}
        className={`${styles.showMoreArrow} ${styles.right}`}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.CIRCLE}
        type={IconType.SECONDARY}
      />
    );
  };

  const onClickLeftArrow = (): void => {
    if (previewItemsContainerRef?.current) {
      const newScrollLeft = previewItemsContainerRef.current.scrollLeft - scrollOffset;
      scrollTo(newScrollLeft);
    }
  };

  const getLeftScrollArrow = (): ReactElement | null => {
    if (!shouldShowLeftArrow) {
      return null;
    }

    return (
      <Icon
        icon="icon-caret-left"
        onClick={onClickLeftArrow}
        className={`${styles.showMoreArrow} ${styles.left}`}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.CIRCLE}
        type={IconType.SECONDARY}
      />
    );
  };

  const areItemsOverflowing = (): boolean => {
    if (designIdsToPublishTo.length === 1) {
      return false;
    }

    if (previewItemsContainerRef?.current) {
      if (previewItemsContainerRef.current.scrollWidth > previewItemsContainerRef.current.clientWidth) {
        return true;
      }
    }

    return false;
  };

  const handlePreviewItemsScroll = (): void => {
    if (!areItemsOverflowing()) {
      setShouldShowLeftArray(false);
      setShouldShowRightArray(false);
      return;
    }

    if (previewItemsContainerRef?.current) {
      const {scrollLeft} = previewItemsContainerRef.current;
      const maxScrollLeft = previewItemsContainerRef.current.scrollWidth - (previewItemsContainerRef.current.scrollLeft + previewItemsContainerRef.current.clientWidth);

      if (scrollLeft === 0) {
        setShouldShowLeftArray(false);
      } else {
        setShouldShowLeftArray(true);
      }

      if (maxScrollLeft <= 0) {
        setShouldShowRightArray(false);
      } else {
        setShouldShowRightArray(true);
      }
    }
  };

  const getDesignPreviewSource = (designId: string): string => {
    if (designsGraphicMetaState[designId] && designsGraphicMetaState[designId].graphicURL !== '') {
      return designsGraphicMetaState[designId].graphicURL;
    }

    return getPosterPlaceholderImagePreviewURL();
  };

  const isVideoPoster = (designId: string): boolean => {
    return !!designsGraphicMetaState[designId]?.isVideo;
  };

  const isPosterLoading = (designId: string): boolean => {
    return !(designsGraphicMetaState[designId] && designsGraphicMetaState[designId].graphicURL !== '');
  };

  const getDesignPreviewAsset = (designId: string): ReactElement => {
    if (!isVideoPoster(designId) || isPosterLoading(designId)) {
      return <img alt={window.i18next.t('pmwjs_design_thumbnail')} src={getDesignPreviewSource(designId)} className={styles.previewImage} />;
    }

    return <video autoPlay muted loop src={getDesignPreviewSource(designId)} className={styles.previewImage} />;
  };

  const generateDesignPreviewId = (designId: string): string => {
    return `${idPrefix}-${designIdsToPublishTo.indexOf(designId)}`;
  };

  const onClickDelete = (designId: string): void => {
    const updatedDesignIds = designIdsToPublishTo.filter((id) => {
      return id !== designId;
    });
    onUpdateDesignIdsToPublishTo(updatedDesignIds);
  };

  const getHoverOverlay = (designId: string): ReactElement => {
    return (
      <div className={styles.hoverOverlay}>
        <Icon
          onClick={() => {
            onClickDelete(designId);
          }}
          className={styles.deleteIcon}
          shape={IconShape.CIRCLE}
          type={IconType.SECONDARY}
          size={IconSize.SIZE_ICON_16}
          icon="icon-delete"
        />
      </div>
    );
  };

  const getDesignPreviewForId = (index: number): ReactElement => {
    return (
      <div key={generateDesignPreviewId(designIdsToPublishTo[index])} className={`${styles.previewWrapper} ${styles.slideIn} ${showDiv ? styles.show : ''}`}>
        <div id={generateDesignPreviewId(designIdsToPublishTo[index])} draggable className={styles.preview}>
          {getDesignPreviewAsset(designIdsToPublishTo[index])}
        </div>
        {getHoverOverlay(designIdsToPublishTo[index])}
      </div>
    );
  };

  const onDragEnd = (sourceId: string, destinationId: string): void => {
    const rearrangedDesignIds: string[] = [...designIdsToPublishTo];
    const sourceIndex = rearrangedDesignIds.indexOf(sourceId);
    const destinationIndex = rearrangedDesignIds.indexOf(destinationId);
    const previewDragged = rearrangedDesignIds[sourceIndex];
    rearrangedDesignIds[sourceIndex] = rearrangedDesignIds[destinationIndex];
    rearrangedDesignIds[destinationIndex] = previewDragged;
    onUpdateDesignIdsToPublishTo(rearrangedDesignIds);
  };

  const onDragStart = (draggedId: string): void => {
    dragPosterIndex.current = parseInt(draggedId, 10);
  };

  const getDesignPreviews = (): ReactElement => {
    const designPreviews: ReactElement[] = [];

    for (let i = 0; i < designIdsToPublishTo.length; i++) {
      designPreviews.push(getDesignPreviewForId(i));
    }

    return (
      <div onScroll={handlePreviewItemsScroll} ref={previewItemsContainerRef} className={`${styles.previewItems} ${isMobileView() ? styles.mobileContainer : ''}`}>
        <DndContext
          sensors={sensors}
          onDragStart={({active}): void => {
            setActive(active);
            onDragStart(String(active.id));
          }}
          onDragEnd={({active, over}): void => {
            if (!over) {
              return;
            }

            if (active.id === over.id) {
              return;
            }
            onDragEnd(String(active.id), String(over.id));
          }}
          onDragCancel={(): void => {
            setActive(null);
          }}
        >
          <SortableContext strategy={horizontalListSortingStrategy} items={designIdsToPublishTo}>
            <div className={styles.draggableClasses}>
              {designPreviews.map((designPreview, index) => {
                return <SocialMediaPreviewItem id={designIdsToPublishTo[index]} item={designPreview} key={`sortable-preview-${index}`}/>;
              })}
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={dropAnimationConfig}>{activeItem ? getDesignPreviewForId(designIdsToPublishTo.indexOf(activeItem)) : null}</DragOverlay>
        </DndContext>
      </div>
    );
  };

  const getGradientOverlay = (classes: string, key: string): ReactElement => {
    return <div key={key} className={`${styles.overlay} ${classes}`} />;
  };

  const getClassesForLeftwardGradient = (): string => {
    if (shouldShowLeftArrow) {
      return styles.leftOverlay;
    }

    return '_hidden';
  };

  const getClassesForRightwardGradient = (): string => {
    if (shouldShowRightArray) {
      return styles.rightOverlay;
    }

    return '_hidden';
  };

  const getGradientOverlays = (): ReactElement[] => {
    return [getGradientOverlay(getClassesForLeftwardGradient(), 'gradient-1'), getGradientOverlay(getClassesForRightwardGradient(), 'gradient-2')];
  };

  const getWrapperClasses = (): string => {
    if (params.xsmall) {
      return styles.extraSmall;
    }

    if (params.small) {
      return styles.small;
    }

    if (params.medium) {
      return styles.medium;
    }

    if (params.large) {
      return styles.large;
    }

    if (params.xlarge) {
      return styles.extralarge;
    }

    return '';
  };

  const isMobileView = (): boolean => {
    return params.xsmall || params.small;
  };

  const getPreviewsContainer = (): ReactElement => {
    return (
      <div key="preview-container-key" className={styles.previewsContainer}>
        {getGradientOverlays()}
        {getLeftScrollArrow()}
        {getRightScrollArrow()}
        {getDesignPreviews()}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`${styles.designsContainer} ${getWrapperClasses()} ${containerClasses}`}>
      <div className={`${styles.wrapper} ${getWrapperClasses()}`}>
        {getTextContainer()} {getPreviewsContainer()}
      </div>
    </div>
  );
}
